import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
const WEBSITE_URL = "/image_url";

export default function FetchCarouselContent(props) {
  const galleryTitle = "gallery-" + props.title;
  const [images] = useState([]);
  const [lastIndex, setLastIndex] = useState(0);
  const [imageObjects, setImageObjects] = useState([]);
  const [status] = useState("");
  const { username } = useAuth();

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateGallery = useCallback(async () => {
    const url = WEBSITE_URL + "/gallery";
    // console.log("Fetching gallery:", { title: galleryTitle, username });
  
    try {
      const response = await axios.get(url, {
        params: { title: galleryTitle, username },
      });
  
      // console.log("Raw response:", response.data);
  
      if (response.data && response.data.length > 0) {
        const images = response.data.map((item, idx) => ({
          url: item.url,
          public_id: item.public_id,
          sortOrder: item.sortOrder ?? idx, 
        }));
  
        // console.log("Images after mapping with sortOrder:", images);
  
        const lastIndex = Math.max(
          ...images.map((obj) => {
            const parts = obj.public_id.split("-");
            return parseInt(parts[parts.length - 1], 10) || 0;
          })
        );
  
        setLastIndex(lastIndex);
        setImageObjects(images);
      } else {
        console.log("No images found.");
      }
    } catch (err) {
      console.error("Failed to fetch gallery:", err);
    }
  }, [galleryTitle, username]);
  

  useEffect(() => {
    const update = async () => {
      await updateGallery(lastIndex, images, status);
    };
    update();
  }, [lastIndex, images, status, updateGallery]);

  useEffect(() => {
    const update = async () => {
      await updateGallery(lastIndex, images, status);
    };
    update();
  }, [updateGallery, images, lastIndex, status]);

  useEffect(() => {
    if (!isViewerOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsViewerOpen(false);
        return;
      }
      if (event.key === "ArrowLeft") {
        setActiveIndex((prev) =>
          prev - 1 < 0 ? imageObjects.length - 1 : prev - 1
        );
      }
      if (event.key === "ArrowRight") {
        setActiveIndex((prev) => (prev + 1) % imageObjects.length);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isViewerOpen, imageObjects.length]);

  const openViewerAt = (index) => {
    if (!imageObjects?.length) return;
    const safeIndex = Math.max(0, Math.min(index, imageObjects.length - 1));
    setActiveIndex(safeIndex);
    setIsViewerOpen(true);
  };

  const filledImageObjects = useMemo(() => {
    const base = Array.isArray(imageObjects) ? imageObjects : [];
    const minCount = 5;
    if (base.length >= minCount) return base;

    const needed = minCount - base.length;
    const placeholders = Array.from({ length: needed }, (_, i) => ({
      url: "/download.jpg",
      public_id: `placeholder-${i}`,
      sortOrder: 9999 + i,
    }));

    return [...base, ...placeholders];
  }, [imageObjects]);

  useEffect(() => {
    if (typeof props?.onHasImagesChange !== "function") return;
    const hasRealImages = Array.isArray(imageObjects) && imageObjects.length > 0;
    props.onHasImagesChange(hasRealImages);
  }, [imageObjects, props]);

  const viewerActive = useMemo(() => {
    return filledImageObjects?.[activeIndex] || null;
  }, [activeIndex, filledImageObjects]);

  const previewIndices = useMemo(() => {
    if (!filledImageObjects?.length) return [];

    // Deterministic-ish pseudo random order based on public_id so it doesn't reshuffle every render.
    const scored = filledImageObjects
      .map((img, idx) => {
        const id = String(img?.public_id || idx);
        let hash = 0;
        for (let i = 0; i < id.length; i += 1) {
          hash = (hash * 31 + id.charCodeAt(i)) % 100000;
        }
        return { idx, score: hash };
      })
      .sort((a, b) => a.score - b.score);

    const maxPreview = Math.min(5, filledImageObjects.length);
    return scored
      .slice(0, Math.min(maxPreview, filledImageObjects.length))
      .map((s) => s.idx);
  }, [filledImageObjects]);

  // const handleReload = async () => {
  //     await updateGallery();
  // }

  return (
    <div className="container">
      {imageObjects.length > 0 && (
        <>
          <GalleryPreview
            imageObjects={filledImageObjects}
            previewIndices={previewIndices}
            onImageClick={openViewerAt}
          />
          {isViewerOpen && viewerActive && (
            <div
              className="gallery-viewer-overlay"
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setIsViewerOpen(false);
              }}
            >
              <div className="gallery-viewer-content">
                <button
                  type="button"
                  className="carousel-arrow left"
                  onClick={() =>
                    setActiveIndex((prev) =>
                      prev - 1 < 0 ? filledImageObjects.length - 1 : prev - 1
                    )
                  }
                  aria-label="Previous"
                >
                  ❮
                </button>

                <div className="gallery-viewer-main">
                  <img
                    src={viewerActive.url}
                    alt="Gallery"
                    className="gallery-viewer-image"
                  />
                  <div className="gallery-viewer-counter">
                    {activeIndex + 1} / {filledImageObjects.length}
                  </div>
                </div>

                <button
                  type="button"
                  className="carousel-arrow right"
                  onClick={() =>
                    setActiveIndex((prev) => (prev + 1) % filledImageObjects.length)
                  }
                  aria-label="Next"
                >
                  ❯
                </button>

                <div className="gallery-viewer-thumbs">
                  {filledImageObjects.map((img, idx) => (
                    <button
                      type="button"
                      key={img.public_id || idx}
                      className={`gallery-viewer-thumb${
                        idx === activeIndex ? " active" : ""
                      }`}
                      onClick={() => setActiveIndex(idx)}
                      aria-label={`Open image ${idx + 1}`}
                    >
                      <img src={img.url} alt="Thumbnail" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const GalleryPreview = ({ imageObjects, previewIndices, onImageClick }) => {
  const preview = previewIndices.map((idx) => ({ idx, img: imageObjects[idx] }));

  const [containByIdx, setContainByIdx] = useState({});

  const handleImgLoad = (idx) => (e) => {
    const el = e?.currentTarget;
    const w = el?.naturalWidth;
    const h = el?.naturalHeight;
    if (!w || !h) return;

    const ratio = w / h;
    const shouldContain = ratio < 0.85 || ratio > 1.85;
    setContainByIdx((prev) => {
      if (prev?.[idx] === shouldContain) return prev;
      return { ...prev, [idx]: shouldContain };
    });
  };

  if (preview.length === 0) return null;

  const main = preview[0];
  const thumbs = preview.slice(1, 5);

  return (
    <div className="gallery-preview-wrapper">
      <div className="gallery-preview-grid">
        {main?.img && (
          <button
            type="button"
            key={main.img?.public_id || `${main.idx}-main`}
            className="gallery-preview-main"
            onClick={() => onImageClick?.(main.idx)}
            aria-label={`Open image ${main.idx + 1}`}
          >
            <img
              src={main.img?.url}
              alt="Gallery"
              onLoad={handleImgLoad(main.idx)}
              className={containByIdx?.[main.idx] ? "is-contain" : ""}
              style={
                containByIdx?.[main.idx]
                  ? {
                      objectFit: "contain",
                      background: "rgba(0, 0, 0, 0.03)",
                    }
                  : { objectFit: "cover" }
              }
            />
          </button>
        )}

        <div className="gallery-preview-thumbs">
          {thumbs.map(({ idx, img }, i) => {
            const isLast = i === thumbs.length - 1;
            return (
              <button
                type="button"
                key={img?.public_id || `${idx}-${i}`}
                className={`gallery-preview-thumb${isLast ? " is-last" : ""}`}
                onClick={() => onImageClick?.(idx)}
                aria-label={`Open image ${idx + 1}`}
              >
                <img
                  src={img?.url}
                  alt="Gallery"
                  onLoad={handleImgLoad(idx)}
                  className={containByIdx?.[idx] ? "is-contain" : ""}
                  style={
                    containByIdx?.[idx]
                      ? {
                          objectFit: "contain",
                          background: "rgba(0, 0, 0, 0.03)",
                        }
                      : { objectFit: "cover" }
                  }
                />
                {isLast && (
                  <span
                    className="gallery-preview-viewall"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick?.(main?.idx ?? idx);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    View all photos
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
